'use client'

import { useLayoutEffect, useSyncExternalStore } from 'react'
import type { ThemeName } from '@roomtreatment/design'
import { THEME_ATTR } from './tokens'
import { THEME_STORAGE_KEY, type ThemePreference } from './boot'

/**
 * Theme preference — client half. The boot script (boot.ts) makes the first
 * decision before paint; everything after that goes through here, so the
 * rules for "what does system mean right now" live in exactly two places that
 * say the same thing.
 */

const CHANGE_EVENT = 'rt-theme-change'
const DARK_QUERY = '(prefers-color-scheme: dark)'

export function readThemePreference(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function resolveTheme(pref: ThemePreference): ThemeName {
  if (pref !== 'system') return pref
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function applyTheme(pref: ThemePreference) {
  const theme = resolveTheme(pref)
  const root = document.documentElement
  root.setAttribute(THEME_ATTR, theme)
  root.style.colorScheme = theme
}

export function setThemePreference(pref: ThemePreference) {
  try {
    if (pref === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
    else localStorage.setItem(THEME_STORAGE_KEY, pref)
  } catch {
    // Storage blocked (private mode, policy): the choice still applies to
    // this page view, it just will not survive a reload.
  }
  applyTheme(pref)
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

function subscribe(onChange: () => void) {
  // `storage` fires for OTHER tabs, so a switch in one tab lands in all of them.
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY) onChange()
  }
  window.addEventListener(CHANGE_EVENT, onChange)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onStorage)
  }
}

/** The stored preference. 'system' on the server and until hydration. */
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, readThemePreference, () => 'system')
}

/**
 * Keeps <html data-theme> true after boot: follows the OS live while the
 * preference is `system` (the OS flipping to dark at sunset flips the page),
 * and picks up a change made in another tab. Mounted once, in the root layout.
 *
 * A LAYOUT effect, so it lands before paint: in development Strict Mode's
 * remount resets <html> to the attributes React manages, wiping the one the
 * boot script set, and a plain effect would show a light frame before
 * re-applying it (Next's preventing-flash-before-hydration guide).
 */
export function ThemeController() {
  const pref = useThemePreference()

  useLayoutEffect(() => {
    applyTheme(pref)
    if (pref !== 'system') return
    const mq = window.matchMedia(DARK_QUERY)
    const onChange = () => applyTheme('system')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [pref])

  return null
}
